import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Calendar,
	Card,
	Table,
	Tag,
	Layout,
	Button,
	Select,
	Space,
	message,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import { fetchKoreanHolidays } from "../../../utilities/koreanHolidays";
import { createCalendarHolidayRenders } from "../calendar/calendarHolidayCellRender";
import LeaveManageNavigation from "./navigation/LeaveManageNavigation";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;

const LEAVE_TYPES_WITH_DATE_RANGE = new Set([
	"출장",
	"연차",
	"경조사",
	"병가",
	"특별휴가",
	"기타",
]);

const CATEGORY_TAG_COLORS = {
	월차: "blue",
	생휴: "red",
	사유서: "orange",
	출장: "green",
	연차: "geekblue",
	경조사: "purple",
	병가: "magenta",
	특별휴가: "gold",
	기타: "lime",
};

function additionalUsersExcludingApplicant(app) {
	const applicantId = app.user_id ?? app.user;
	const list = app.additional_users_info || [];
	if (applicantId == null) {
		return list;
	}
	return list.filter((u) => u.id !== applicantId);
}

function flattenToRows(applications) {
	const rows = [];
	for (const app of applications) {
		if (!app.is_applied || app.is_cancelled) {
			continue;
		}
		const dateStr = app.leave_date;
		const base = { leave_date: dateStr };

		if (
			LEAVE_TYPES_WITH_DATE_RANGE.has(
				app.leave_type,
			) ||
			app.leave_type === "사유서"
		) {
			const s = app.start_date || app.leave_date;
			const e =
				app.end_date ||
				app.start_date ||
				app.leave_date;
			let cursor = dayjs(s);
			const endD = dayjs(e);
			if (!cursor.isValid() || !endD.isValid()) {
				continue;
			}
			const rangeReason =
				app.reason && String(app.reason).trim()
					? String(app.reason).trim()
					: "-";
			const reasonCategory =
				app.leave_type === "사유서"
					? app.reason_category || "—"
					: null;
			const tagColor =
				app.leave_type === "사유서"
					? "orange"
					: CATEGORY_TAG_COLORS[
							app.leave_type
					  ] || "default";
			while (!cursor.isAfter(endD, "day")) {
				const dStr = cursor.format("YYYY-MM-DD");
				rows.push({
					leave_date: dStr,
					key: `${app.id}-${dStr}`,
					name: app.user_name,
					category: app.leave_type,
					tagColor,
					reason_category: reasonCategory,
					reason: rangeReason,
				});
				for (const u of additionalUsersExcludingApplicant(
					app,
				)) {
					rows.push({
						leave_date: dStr,
						key: `${app.id}-${dStr}-add-${u.id}`,
						name: u.name,
						category: app.leave_type,
						tagColor,
						reason_category: reasonCategory,
						reason: rangeReason,
					});
				}
				cursor = cursor.add(1, "day");
			}
			continue;
		} else {
			const tagColor =
				CATEGORY_TAG_COLORS[app.leave_type] ||
				"default";
			rows.push({
				...base,
				key: `${app.id}`,
				name: app.user_name,
				category: app.leave_type,
				tagColor,
				reason_category: null,
				reason: "-",
			});
			for (const u of additionalUsersExcludingApplicant(
				app,
			)) {
				rows.push({
					...base,
					key: `${app.id}-add-${u.id}`,
					name: u.name,
					category: app.leave_type,
					tagColor,
					reason_category: null,
					reason: "-",
				});
			}
		}
	}

	rows.sort((a, b) => {
		const da =
			dayjs(a.leave_date).valueOf() -
			dayjs(b.leave_date).valueOf();
		if (da !== 0) {
			return da;
		}
		const catCmp = (a.category || "").localeCompare(
			b.category || "",
			"ko",
		);
		if (catCmp !== 0) {
			return catCmp;
		}
		return (a.name || "").localeCompare(b.name || "", "ko");
	});
	return rows;
}

const LeaveApplicationStatus = () => {
	const [applications, setApplications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [calendarPanelDate, setCalendarPanelDate] = useState(() =>
		dayjs(),
	);
	const [holidayMap, setHolidayMap] = useState({});
	const [isStackedLayout, setIsStackedLayout] = useState(
		() => typeof window !== "undefined" && window.innerWidth <= 992,
	);

	useEffect(() => {
		const onResize = () => {
			setIsStackedLayout(window.innerWidth <= 992);
		};
		onResize();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const fetchApplications = useCallback(async () => {
		try {
			setLoading(true);
			const y = calendarPanelDate.year();
			const m = calendarPanelDate.month() + 1;
			const response = await useAxios.get(
				`/utils/leave/applications/?year=${y}&month=${m}`,
			);
			if (response.status === 200) {
				setApplications(response.data);
			}
		} catch (error) {
			message.error("신청 현황을 불러오는데 실패했습니다.");
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [calendarPanelDate]);

	useEffect(() => {
		fetchApplications();
	}, [fetchApplications]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const y = calendarPanelDate.year();
				const m = calendarPanelDate.month() + 1;
				const map = await fetchKoreanHolidays(y, m);
				if (!cancelled) {
					setHolidayMap(map);
				}
			} catch (e) {
				console.error(e);
				if (!cancelled) {
					setHolidayMap({});
				}
				message.warning(
					"공휴일 정보를 불러오지 못했습니다. 주말만 구분됩니다.",
				);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [calendarPanelDate]);

	const tableRows = useMemo(
		() => flattenToRows(applications),
		[applications],
	);

	const rowsByDate = useMemo(() => {
		const map = {};
		for (const row of tableRows) {
			const d = dayjs(row.leave_date).format("YYYY-MM-DD");
			if (!map[d]) {
				map[d] = [];
			}
			map[d].push(row);
		}
		return map;
	}, [tableRows]);

	const selectedDateKey = calendarPanelDate.format("YYYY-MM-DD");
	const selectedDayTableRows = useMemo(() => {
		return rowsByDate[selectedDateKey] || [];
	}, [rowsByDate, selectedDateKey]);

	const renderDateContent = useCallback(
		(d) => {
			const key = d.format("YYYY-MM-DD");
			const dayRows = rowsByDate[key] || [];
			const categoryToNames = {};
			for (const row of dayRows) {
				if (!categoryToNames[row.category]) {
					categoryToNames[row.category] =
						new Set();
				}
				categoryToNames[row.category].add(row.name);
			}
			const categoryOrder = Object.keys(categoryToNames).sort(
				(a, b) => a.localeCompare(b, "ko"),
			);
			return (
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						gap: 4,
						alignContent: "flex-start",
					}}>
					{categoryOrder.map((cat) => (
						<Tag
							key={cat}
							color={
								CATEGORY_TAG_COLORS[
									cat
								] || "default"
							}
							style={{ margin: 0 }}>
							{cat}{" "}
							{
								categoryToNames[
									cat
								].size
							}
							명
						</Tag>
					))}
				</div>
			);
		},
		[rowsByDate],
	);

	const { fullCellRender } = useMemo(
		() =>
			createCalendarHolidayRenders({
				holidayMap,
				renderDateContent,
			}),
		[holidayMap, renderDateContent],
	);

	const tableColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			width: "20%",
			render: (text, record) => (
				<div style={{ fontSize: 14, fontWeight: 600 }}>
					{text}
				</div>
			),
		},
		{
			title: "구분",
			dataIndex: "category",
			key: "category",
			width: "18%",
			render: (text, record) => (
				<Tag color={record.tagColor}>{text}</Tag>
			),
		},
		{
			title: "세부분류",
			dataIndex: "reason_category",
			key: "reason_category",
			width: "18%",
			render: (text) => text || "—",
		},
		{
			title: "사유",
			dataIndex: "reason",
			key: "reason",
			width: "44%",
			ellipsis: true,
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="월차ㆍ사유서 신청 현황" />
			<Layout style={{ minHeight: "100vh" }}>
				<LeaveManageNavigation />
				<Layout>
					<Content className="app-page-content">
						<div className="utils-rent_sanctuary__container page">
							<PageHeader title="월차ㆍ사유서 신청 현황" />
							<div
								style={{
									display: "flex",
									flexDirection:
										isStackedLayout
											? "column"
											: "row",
									flexWrap: "nowrap",
									gap: 16,
									alignItems: "flex-start",
									width: "100%",
								}}>
								<Card
									className="surface-card"
									style={
										isStackedLayout
											? {
													width: "100%",
													alignSelf: "stretch",
												}
											: {
													flex: "0 0 440px",
													width: 480,
													minWidth: 280,
													maxWidth: 520,
													marginTop: 20,
												}
									}>
									{/* header with selected date */}
									<div
										style={{
											fontSize: 16,
											fontWeight: 600,
											marginBottom: 20,
											display: "flex",
											alignItems: "center",
											justifyContent:
												"center",
										}}>
										{dayjs(
											selectedDateKey,
										).format(
											"YYYY년 MM월 DD일",
										)}
									</div>
									<Table
										size="small"
										tableLayout="fixed"
										columns={
											tableColumns
										}
										dataSource={
											selectedDayTableRows
										}
										loading={
											loading
										}
										pagination={{
											pageSize: 15,
											showSizeChanger: false,
										}}
										sticky={{
											offsetHeader: 64,
										}}
										locale={{
											emptyText: "선택한 날짜에 표시할 내역이 없습니다.",
										}}
									/>
								</Card>
								<Card
									className="utils-rent_sanctuary__calendar-card"
									style={
										isStackedLayout
											? {
													width: "100%",
													alignSelf: "stretch",
												}
											: {
													flex: "1 1 0%",
													minWidth: 0,
													width: "auto",
												}
									}>
									<div className="utils-rent_car_calendar-scope">
										<Calendar
											className="utils-calendar-hide-outside-month-week"
											value={
												calendarPanelDate
											}
											fullCellRender={
												fullCellRender
											}
											mode="month"
											onChange={(
												d,
											) =>
												setCalendarPanelDate(
													d,
												)
											}
											onPanelChange={(
												d,
											) =>
												setCalendarPanelDate(
													d,
												)
											}
											headerRender={({
												value,
												onChange,
											}) => {
												const year =
													value.year();
												const month =
													value.month();
												const months =
													[];
												const yearOptions =
													[];
												for (
													let i = 0;
													i <
													12;
													i++
												) {
													months.push(
														<Select.Option
															key={
																i
															}
															value={
																i
															}>
															{i +
																1}

															월
														</Select.Option>,
													);
												}
												for (
													let i =
														year -
														10;
													i <
													year +
														10;
													i++
												) {
													yearOptions.push(
														<Select.Option
															key={
																i
															}
															value={
																i
															}>
															{
																i
															}

															년
														</Select.Option>,
													);
												}
												return (
													<Space
														style={{
															padding: 8,
														}}>
														<Button
															size="middle"
															onClick={() => {
																const next =
																	value.subtract(
																		1,
																		"month",
																	);
																onChange(
																	next,
																);
																setCalendarPanelDate(
																	next,
																);
															}}>
															<LeftOutlined />
														</Button>
														<Select
															size="middle"
															value={
																year
															}
															onChange={(
																newYear,
															) => {
																const now =
																	value
																		.clone()
																		.year(
																			newYear,
																		);
																onChange(
																	now,
																);
																setCalendarPanelDate(
																	now,
																);
															}}
															style={{
																width: 100,
															}}>
															{
																yearOptions
															}
														</Select>
														<Select
															size="middle"
															value={
																month
															}
															onChange={(
																newMonth,
															) => {
																const now =
																	value
																		.clone()
																		.month(
																			newMonth,
																		);
																onChange(
																	now,
																);
																setCalendarPanelDate(
																	now,
																);
															}}
															style={{
																width: 80,
															}}>
															{
																months
															}
														</Select>
														<Button
															size="middle"
															onClick={() => {
																const now =
																	dayjs();
																onChange(
																	now,
																);
																setCalendarPanelDate(
																	now,
																);
															}}>
															오늘
														</Button>
														<Button
															size="middle"
															onClick={() => {
																const next =
																	value.add(
																		1,
																		"month",
																	);
																onChange(
																	next,
																);
																setCalendarPanelDate(
																	next,
																);
															}}>
															<RightOutlined />
														</Button>
													</Space>
												);
											}}
											locale={{
												lang: {
													locale: "ko",
													monthBeforeYear: true,
													yearFormat: "YYYY년",
													monthFormat:
														"M월",
													weekFormat: "wo",
													weekStart: 0,
													shortWeekDays:
														[
															"일",
															"월",
															"화",
															"수",
															"목",
															"금",
															"토",
														],
													shortMonths:
														[
															"1월",
															"2월",
															"3월",
															"4월",
															"5월",
															"6월",
															"7월",
															"8월",
															"9월",
															"10월",
															"11월",
															"12월",
														],
												},
											}}
										/>
									</div>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default LeaveApplicationStatus;
